import { useEffect, useState } from "react";
import {
  chakraUseBreakpointValue,
  ChakraBox,
  ChakraFlex,
  ChakraSlide,
  ChakraButton,
  ChakraLink,
  Logos,
} from "@airhelp/react-ui-components";
import FlagBox from "components/LanguageSwitcher/FlagBox";
import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useLanguage } from "contexts/LocalizationContext";
import { useModal } from "contexts/ModalContext";
import { useFlagBoxSettings } from "contexts/SettingsContext";
import { useMobileMenuStore } from "stores/MobileMenuStore";
import { useReturningUsersNotification, useDetectScrollDirection } from "hooks";
import throttle from "lodash.throttle";
import { TopBanner } from "modules/common/components/TopBanner";
import NextLink from "next/link";
import { HamburgerMenuIcon } from "styles/icons";
import ReturningUsersNotification from "../ReturningUsersNotification";
import { TOP_MENU_LANG_MODAL_OPEN_EVENT_UA, TOP_MENU_LANG_MODAL_OPEN_EVENT } from "../../trackingConsts";
import type { HeaderNavProps } from "./HeaderNav.d";
import { MobileMenuContent } from "./MenuItems";

const { Airhelp: AirHelpLogo } = Logos;

export const HeaderNav = ({
  logo: Logo = AirHelpLogo,
  menuItems,
  headerButtons,
  topBannerText = "",
  topBannerButtonText = "",
  subMenu = null,
}: HeaderNavProps) => {
  const [slideOpen, setSlideOpen] = useState(true);
  const [smallNav, setSmallNav] = useState(false);
  const [position, setPosition] = useState<"sticky" | "fixed">("fixed");
  const language = useLanguage();
  const { openModal } = useModal();
  const { openMobileMenu } = useMobileMenuStore();
  const hideFlagBox = useFlagBoxSettings();
  const { isOpen: isNotificationVisible } = useReturningUsersNotification();

  const scrollDirection = useDetectScrollDirection("80");

  const navBoxShadow = smallNav ? "1px 4px 10px rgba(203, 202, 209, 0.25)" : "none";

  const logoSmallWidth = "95px";
  const logoLargeWidth = "118px";

  const logoWidth = { base: logoSmallWidth, lg: smallNav ? logoSmallWidth : logoLargeWidth };

  const topBannerComponent = topBannerText && topBannerButtonText && (
    <TopBanner bannerText={topBannerText} buttonText={topBannerButtonText} />
  );

  const navOnScroll = () => {
    setSmallNav(window.pageYOffset > 0);
  };

  const throttledResizeListener = throttle(navOnScroll, 50);

  useEffect(() => {
    setPosition(isNotificationVisible ? "sticky" : "fixed");
  }, [isNotificationVisible]);

  useEffect(() => {
    setSlideOpen(scrollDirection !== "down");

    window.addEventListener("scroll", throttledResizeListener);

    return () => window.removeEventListener("scroll", throttledResizeListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDirection]);

  return (
    <>
      <ReturningUsersNotification />
      <ChakraSlide
        initial="visible"
        direction="top"
        in={chakraUseBreakpointValue({ base: slideOpen, lg: true })}
        style={{ zIndex: 4, position }}
      >
        {topBannerComponent}
        <ChakraFlex
          wrap="wrap"
          px={{ base: 4, xl: 16 }}
          color="greyscale.600"
          backgroundColor="white"
          py={4}
          mb={-20}
          boxShadow={navBoxShadow}
        >
          <ChakraFlex
            width="full"
            as="nav"
            role="menubar"
            maxWidth={MAX_GRID_WIDTH}
            mx="auto"
            justify={{ base: "space-between", lg: "unset" }}
          >
            <ChakraLink as={NextLink} href={`/${language}/`} passHref>
              <Logo minW={logoWidth} transition="0.2s" minH={{ base: "35px", lg: "40px" }} />
            </ChakraLink>

            <ChakraBox
              display={{ base: "none", lg: "flex" }}
              width={{ base: "full", md: "auto" }}
              justifyContent="flex-end"
              alignItems="center"
              flexGrow={1}
              mr={hideFlagBox && 5}
            >
              {menuItems}
            </ChakraBox>

            {!hideFlagBox && (
              <ChakraBox ml={{ base: "auto", lg: 1, xl: 9 }} mr={{ base: 3, lg: 0 }}>
                <FlagBox openModal={openModal} gaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT} uaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT_UA} />
              </ChakraBox>
            )}

            <ChakraBox display={{ base: "none", lg: "flex" }} mt={{ base: 4, md: 0 }}>
              {headerButtons}
            </ChakraBox>

            {subMenu && <ChakraBox display={{ base: "block", md: "none" }}>{subMenu}</ChakraBox>}

            <ChakraButton
              variant="unstyled"
              _hover={{}}
              _active={{}}
              display={{ base: "flex", lg: "none" }}
              onClick={() => openMobileMenu(<MobileMenuContent headerButtons={headerButtons} menuItems={menuItems} />)}
              data-testid="mobile-menu-button"
              aria-label="Open site menu"
            >
              <HamburgerMenuIcon color="primary.900" w={6} h={4} />
            </ChakraButton>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraSlide>
    </>
  );
};
